// PrecoPorCanal.tsx
//
// Não existe preço por canal no dado da Central: o preço é UM número por
// produto dentro da categoria (`companyInfoValue.products[].productPrice`), sem
// nenhum campo de canal ao lado. Esta tela não finge o contrário — ela mostra o
// contorno que a rede inventou para viver sem isso: duplicar o produto com o
// prefixo "IF" para ter o preço do iFood.
//
// O par (item "IF" ↔ item sem prefixo) é achado por NOME, não por código: são
// dois cadastros independentes, com códigos diferentes, e nada no dado liga um
// ao outro. Por isso a coluna do par pode vir vazia — e vir vazia é informação.
import React, { useMemo, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/cardapioSection/index";
import { Cardapio, ProdutoCardapio, formatarReais } from "./regrasDeSaneamento";

interface Props {
  cardapio: Cardapio;
}

interface ParDeCanal {
  chave: string;
  copia: ProdutoCardapio;
  base: ProdutoCardapio | null;
  diferenca: number | null;
  percentual: number | null;
}

const PARES_POR_VEZ = 20;

const PrecoPorCanal: React.FC<Props> = ({ cardapio }) => {
  const [limite, setLimite] = useState(PARES_POR_VEZ);

  const pares = useMemo<ParDeCanal[]>(() => {
    const porNome: Record<string, ProdutoCardapio> = {};
    for (const produto of cardapio.produtos) {
      porNome[produto.nome.toUpperCase()] = produto;
    }

    return cardapio.produtos
      .filter((produto) => produto.nome.toUpperCase().startsWith("IF "))
      .map((copia) => {
        const base = porNome[copia.nome.toUpperCase().slice(3).trim()] || null;
        const temOsDois =
          base !== null && base.preco !== null && copia.preco !== null;
        const diferenca = temOsDois
          ? (copia.preco as number) - (base as ProdutoCardapio).preco!
          : null;
        return {
          chave: copia.uuid || copia.codigo,
          copia,
          base,
          diferenca,
          // Percentual sobre o preço do salão. Preço base zerado não vira
          // divisão por zero: sem base não há percentual, e a célula diz isso.
          percentual:
            diferenca !== null && (base as ProdutoCardapio).preco
              ? (diferenca / ((base as ProdutoCardapio).preco as number)) * 100
              : null,
        };
      });
  }, [cardapio]);

  const comPar = pares.filter((par) => par.base !== null);
  const semPar = pares.length - comPar.length;
  const comPrecoDiferente = comPar.filter(
    (par) => par.diferenca !== null && par.diferenca !== 0
  ).length;
  const visiveis = pares.slice(0, limite);

  return (
    <>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              price_change
            </AlfredoStyles.MaterialIcons>
            Preço por canal
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            O que a Central tem de preço, e o que ela não tem
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      <AlfredoStyles.Cartao>
        <AlfredoStyles.CartaoTitulo>
          <AlfredoStyles.MaterialIcons>block</AlfredoStyles.MaterialIcons>
          Preço por canal NÃO existe no dado da Central
        </AlfredoStyles.CartaoTitulo>
        <AlfredoStyles.CartaoTexto>
          O preço é um número só, gravado dentro da categoria, na empresa. Não
          há campo de canal ao lado dele — nem salão, nem balcão, nem delivery
          próprio, nem iFood. Também não há preço por loja: a linha que a
          unidade guarda para a mesma categoria carrega a impressora de produção
          e de entrega, não o preço. Todas as lojas da empresa vendem pelo mesmo
          número.
        </AlfredoStyles.CartaoTexto>
        <AlfredoStyles.CartaoTexto>
          Consequência prática: não existe "aplicar este preço só no delivery" a
          partir da Central, e esta tela não oferece esse botão.
        </AlfredoStyles.CartaoTexto>
      </AlfredoStyles.Cartao>

      <AlfredoStyles.SecaoTitulo>
        <AlfredoStyles.MaterialIcons>call_split</AlfredoStyles.MaterialIcons>
        O contorno que a rede usa hoje
      </AlfredoStyles.SecaoTitulo>

      {pares.length === 0 ? (
        <AlfredoStyles.Aviso $tom="ok">
          Este cardápio não tem produtos duplicados com o prefixo "IF". Se a rede
          precisa de preço diferente por canal, hoje ela não está resolvendo isso
          por duplicação de cadastro.
        </AlfredoStyles.Aviso>
      ) : (
        <>
          <AlfredoStyles.GradeIndicadores>
            <AlfredoStyles.Indicador>
              <AlfredoStyles.IndicadorNumero $tom="alerta">
                {pares.length}
              </AlfredoStyles.IndicadorNumero>
              <AlfredoStyles.IndicadorRotulo>
                Cópias com prefixo "IF"
              </AlfredoStyles.IndicadorRotulo>
              <AlfredoStyles.IndicadorNota>
                {Math.round((pares.length / (cardapio.produtos.length || 1)) * 100)}
                % do cadastro de produtos da rede.
              </AlfredoStyles.IndicadorNota>
            </AlfredoStyles.Indicador>

            <AlfredoStyles.Indicador>
              <AlfredoStyles.IndicadorNumero>
                {comPrecoDiferente}
              </AlfredoStyles.IndicadorNumero>
              <AlfredoStyles.IndicadorRotulo>
                Com preço diferente do original
              </AlfredoStyles.IndicadorRotulo>
              <AlfredoStyles.IndicadorNota>
                É a prova de que a duplicação existe para ter outro preço, e não
                por engano de carga.
              </AlfredoStyles.IndicadorNota>
            </AlfredoStyles.Indicador>

            <AlfredoStyles.Indicador>
              <AlfredoStyles.IndicadorNumero
                $tom={semPar > 0 ? "alerta" : "neutro"}
              >
                {semPar}
              </AlfredoStyles.IndicadorNumero>
              <AlfredoStyles.IndicadorRotulo>
                Sem item equivalente
              </AlfredoStyles.IndicadorRotulo>
              <AlfredoStyles.IndicadorNota>
                Cópias que não têm mais o produto original com o mesmo nome:
                existem só no canal, ou o original mudou de nome.
              </AlfredoStyles.IndicadorNota>
            </AlfredoStyles.Indicador>
          </AlfredoStyles.GradeIndicadores>

          <AlfredoStyles.Aviso $tom="atencao">
            Cada alteração de preço tem de ser feita duas vezes, uma em cada
            cópia. Quando alguém esquece a segunda, o cliente vê um preço no
            aplicativo e outro no salão — e o relatório mostra o mesmo produto
            partido em dois códigos.
          </AlfredoStyles.Aviso>

          <AlfredoStyles.TabelaEnvolucro style={{ marginTop: "var(--sp-12)" }}>
            <AlfredoStyles.Tabela>
              <thead>
                <tr>
                  <AlfredoStyles.Th>Cópia "IF"</AlfredoStyles.Th>
                  <AlfredoStyles.Th $direita>Preço da cópia</AlfredoStyles.Th>
                  <AlfredoStyles.Th>Item original</AlfredoStyles.Th>
                  <AlfredoStyles.Th $direita>Preço do original</AlfredoStyles.Th>
                  {/* "R$" e "%" em COLUNAS separadas, nunca no mesmo texto. */}
                  <AlfredoStyles.Th $direita>Diferença (R$)</AlfredoStyles.Th>
                  <AlfredoStyles.Th $direita>Diferença (%)</AlfredoStyles.Th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((par) => (
                  <AlfredoStyles.Tr key={par.chave}>
                    <AlfredoStyles.Td>
                      <AlfredoStyles.RegistroCodigo>
                        {par.copia.codigo}
                      </AlfredoStyles.RegistroCodigo>{" "}
                      {par.copia.nome}
                    </AlfredoStyles.Td>
                    <AlfredoStyles.Td $direita $mono>
                      {formatarReais(par.copia.preco)}
                    </AlfredoStyles.Td>
                    <AlfredoStyles.Td>
                      {par.base ? (
                        <>
                          <AlfredoStyles.RegistroCodigo>
                            {par.base.codigo}
                          </AlfredoStyles.RegistroCodigo>{" "}
                          {par.base.nome}
                        </>
                      ) : (
                        <AlfredoStyles.Etiqueta $tom="media">
                          não encontrado
                        </AlfredoStyles.Etiqueta>
                      )}
                    </AlfredoStyles.Td>
                    <AlfredoStyles.Td $direita $mono>
                      {par.base ? formatarReais(par.base.preco) : "—"}
                    </AlfredoStyles.Td>
                    <AlfredoStyles.Td $direita $mono>
                      {par.diferenca === null
                        ? "—"
                        : formatarReais(par.diferenca)}
                    </AlfredoStyles.Td>
                    <AlfredoStyles.Td $direita $mono>
                      {par.percentual === null
                        ? "—"
                        : `${par.percentual.toFixed(1).replace(".", ",")}%`}
                    </AlfredoStyles.Td>
                  </AlfredoStyles.Tr>
                ))}
              </tbody>
            </AlfredoStyles.Tabela>
          </AlfredoStyles.TabelaEnvolucro>

          {visiveis.length < pares.length && (
            <AlfredoStyles.BotaoTexto onClick={() => setLimite(pares.length)}>
              <AlfredoStyles.MaterialIcons
                style={{ fontSize: "var(--fs-base)" }}
              >
                expand_more
              </AlfredoStyles.MaterialIcons>
              Ver todos os {pares.length} pares
            </AlfredoStyles.BotaoTexto>
          )}
        </>
      )}

      <AlfredoStyles.Cartao>
        <AlfredoStyles.CartaoTitulo>
          <AlfredoStyles.MaterialIcons>construction</AlfredoStyles.MaterialIcons>
          O que faltaria para esta tela virar edição
        </AlfredoStyles.CartaoTitulo>
        <AlfredoStyles.CartaoTexto>
          Um campo de canal no preço (o produto precisaria de uma lista de
          preços, uma por canal, e não de um número só), e uma rota da Central
          para gravá-lo, com a carga passando a ir também da Central para o OPS.
          Nada disso existe hoje. Até existir, o preço se edita no OPS.
        </AlfredoStyles.CartaoTexto>
      </AlfredoStyles.Cartao>
    </>
  );
};

export default PrecoPorCanal;
